// ============================================================================
// 자동화 규칙 React Query 훅 (CHG-047)
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as automationApi from '../services/automationService';
import type { CreateRuleData } from '../services/automationService';

export const automationKeys = {
  rules: (projectId: string) => ['automation', 'rules', projectId] as const,
  logs: (projectId: string, params?: Record<string, string>) => ['automation', 'logs', projectId, params] as const,
  templates: (projectId: string) => ['automation', 'templates', projectId] as const,
};

export function useAutomationRules(projectId: string) {
  return useQuery({
    queryKey: automationKeys.rules(projectId),
    queryFn: () => automationApi.fetchRules(projectId),
    enabled: !!projectId,
  });
}

export function useCreateRule(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateRuleData) => automationApi.createRule(projectId, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: automationKeys.rules(projectId) }); },
  });
}

export function useUpdateRule(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: string; data: Partial<CreateRuleData> & { isEnabled?: boolean } }) =>
      automationApi.updateRule(projectId, ruleId, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: automationKeys.rules(projectId) }); },
  });
}

export function useDeleteRule(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => automationApi.deleteRule(projectId, ruleId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: automationKeys.rules(projectId) }); },
  });
}

export function useToggleRule(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ruleId: string) => automationApi.toggleRule(projectId, ruleId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: automationKeys.rules(projectId) }); },
  });
}

export function useAutomationLogs(projectId: string, params?: Record<string, string>) {
  return useQuery({
    queryKey: automationKeys.logs(projectId, params),
    queryFn: () => automationApi.fetchLogs(projectId, params),
    enabled: !!projectId,
  });
}

export function useAutomationTemplates(projectId: string) {
  return useQuery({
    queryKey: automationKeys.templates(projectId),
    queryFn: () => automationApi.fetchTemplates(projectId),
    enabled: !!projectId,
  });
}

export function useApplyTemplate(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateType: string) => automationApi.applyTemplate(projectId, templateType),
    onSuccess: () => { qc.invalidateQueries({ queryKey: automationKeys.rules(projectId) }); },
  });
}
